import { useState, useMemo } from "react";
import { MapPin, Car, Wrench, ArrowRight, RefreshCw, CheckCircle2, Sparkles } from "lucide-react";
import { GARAGE_SIZES } from "../data/siteData";

// Ballpark pricing (Phoenix Metro, 2025). These are honest ranges the sales team
// can confirm/adjust after satellite measurement.
const SIZE_TABLE = {
  "1 Car":       { sqftMin: 220,  sqftMax: 280,  perSqftMin: 7.0, perSqftMax: 9.5 },
  "2 Car":       { sqftMin: 400,  sqftMax: 480,  perSqftMin: 6.5, perSqftMax: 8.5 },
  "3 Car":       { sqftMin: 600,  sqftMax: 720,  perSqftMin: 6.0, perSqftMax: 8.0 },
  "4+ Car / RV": { sqftMin: 800,  sqftMax: 1100, perSqftMin: 5.8, perSqftMax: 7.5 },
};

const CONDITION_OPTIONS = [
  { id: "great",  label: "Great",     desc: "Newer slab, no visible damage.",                          adder: [0, 0] },
  { id: "worn",   label: "Some Wear", desc: "Cracks, stains, or minor spalling.",                     adder: [250, 550] },
  { id: "rough",  label: "Rough",     desc: "Failed epoxy, heavy pitting, or major crack repair.",     adder: [700, 1600] },
];

const round50 = (n) => Math.round(n / 50) * 50;

const formatUSD = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const StepDot = ({ active, done, num }) => (
  <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold border transition-colors ${done ? "bg-[#028090] border-[#028090] text-white" : active ? "border-[#028090] text-[#028090]" : "border-white/25 text-gray-500"}`}>
    {done ? <CheckCircle2 size={14} /> : num}
  </div>
);

export default function InstantEstimator({ onLockInPrice }) {
  const [step, setStep] = useState(1);
  const [zip, setZip] = useState("");
  const [size, setSize] = useState("");
  const [condition, setCondition] = useState("");

  const result = useMemo(() => {
    if (!size || !condition) return null;
    const s = SIZE_TABLE[size];
    const c = CONDITION_OPTIONS.find((o) => o.id === condition);
    if (!s || !c) return null;
    const low  = round50(s.sqftMin * s.perSqftMin + c.adder[0]);
    const high = round50(s.sqftMax * s.perSqftMax + c.adder[1]);
    return { low, high, sqftLow: s.sqftMin, sqftHigh: s.sqftMax };
  }, [size, condition]);

  const reset = () => { setStep(1); setZip(""); setSize(""); setCondition(""); };

  const lockIn = () => {
    if (!result) return;
    onLockInPrice?.({
      zip,
      garage_size: size,
      condition,
      message: `Instant Estimate: ${formatUSD(result.low)}–${formatUSD(result.high)} for a ${size} garage in ${condition} condition${zip ? ` (ZIP ${zip})` : ""}.`,
    });
  };

  if (result && step === 4) {
    return (
      <div className="border border-[#028090]/40 bg-gradient-to-br from-[#028090]/15 to-transparent p-8 md:p-10 relative overflow-hidden" data-testid="instant-estimate-result">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#028090]/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 text-[#E8B86D] text-xs uppercase tracking-[0.2em] font-bold mb-3">
            <Sparkles size={14} /> Instant Estimate
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">Your Ballpark:</h3>
          <p className="font-display text-4xl sm:text-5xl font-extrabold text-white" data-testid="estimate-range">
            {formatUSD(result.low)} <span className="text-[#028090]">–</span> {formatUSD(result.high)}
          </p>
          <p className="text-gray-400 text-sm mt-3">
            Based on a <strong className="text-white">{size}</strong> garage (~{result.sqftLow}–{result.sqftHigh} sq ft) in <strong className="text-white">{CONDITION_OPTIONS.find(o => o.id === condition)?.label.toLowerCase()}</strong> condition{zip ? `, ZIP ${zip}` : ""}.
          </p>
          <div className="mt-6 border-t border-white/10 pt-6 space-y-3">
            <p className="flex items-start gap-2.5 text-gray-300 text-sm"><CheckCircle2 size={16} className="text-[#028090] mt-0.5 shrink-0" /> Flat-rate written pricing confirmed after free satellite measurement</p>
            <p className="flex items-start gap-2.5 text-gray-300 text-sm"><CheckCircle2 size={16} className="text-[#028090] mt-0.5 shrink-0" /> One-day install, drive on in 24 hours</p>
            <p className="flex items-start gap-2.5 text-gray-300 text-sm"><CheckCircle2 size={16} className="text-[#028090] mt-0.5 shrink-0" /> Lifetime limited warranty in writing</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <button onClick={lockIn} data-testid="lock-in-price-btn" className="bg-[#028090] text-white hover:bg-[#026c7a] transition-colors duration-300 px-6 py-3.5 uppercase font-bold tracking-wider text-sm flex-1 flex items-center justify-center gap-2">
              Lock In This Price <ArrowRight size={16} />
            </button>
            <button onClick={reset} data-testid="estimator-restart-btn" className="border border-white/25 text-gray-300 hover:border-[#028090] hover:text-[#028090] transition-colors duration-300 px-5 py-3.5 uppercase font-bold tracking-wider text-xs flex items-center justify-center gap-2">
              <RefreshCw size={14} /> Restart
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-4">Ballpark only. Final price is fixed in writing after our free satellite measurement — never higher than the range shown.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-white/10 bg-white/5 p-8 md:p-10" data-testid="instant-estimator">
      <div className="flex items-center gap-2 text-[#E8B86D] text-xs uppercase tracking-[0.2em] font-bold mb-2">
        <Sparkles size={14} /> Instant Estimate · 30 seconds
      </div>
      <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-6">Get your ballpark price now.</h3>

      <div className="flex items-center gap-3 mb-8">
        <StepDot num={1} active={step === 1} done={step > 1} />
        <div className={`flex-1 h-px ${step > 1 ? "bg-[#028090]" : "bg-white/10"}`} />
        <StepDot num={2} active={step === 2} done={step > 2} />
        <div className={`flex-1 h-px ${step > 2 ? "bg-[#028090]" : "bg-white/10"}`} />
        <StepDot num={3} active={step === 3} done={step > 3} />
      </div>

      {step === 1 && (
        <div data-testid="estimator-step-1">
          <label className="flex items-center gap-2 text-white font-semibold mb-3 text-sm"><MapPin size={16} className="text-[#028090]" strokeWidth={1.5} /> What's your ZIP code?</label>
          <input
            type="text" inputMode="numeric" maxLength={5}
            data-testid="estimator-zip-input"
            placeholder="85260"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, ""))}
            className="w-full bg-[#000000] border border-white/20 text-white placeholder-gray-600 p-4 focus:ring-2 focus:ring-[#028090] focus:border-transparent transition-all outline-none text-lg tracking-widest"
            onKeyDown={(e) => { if (e.key === "Enter" && zip.length >= 5) setStep(2); }}
          />
          <p className="text-gray-500 text-xs mt-2">We use satellite imagery to measure your garage — no in-person visit needed.</p>
          <button
            onClick={() => setStep(2)}
            disabled={zip.length < 5}
            data-testid="estimator-next-1"
            className="w-full mt-6 bg-[#028090] text-white hover:bg-[#026c7a] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300 px-6 py-3.5 uppercase font-bold tracking-wider text-sm flex items-center justify-center gap-2"
          >
            Continue <ArrowRight size={16} />
          </button>
        </div>
      )}

      {step === 2 && (
        <div data-testid="estimator-step-2">
          <label className="flex items-center gap-2 text-white font-semibold mb-4 text-sm"><Car size={16} className="text-[#028090]" strokeWidth={1.5} /> How many cars fit in your garage?</label>
          <div className="grid grid-cols-2 gap-3">
            {GARAGE_SIZES.map((g) => (
              <button
                key={g}
                onClick={() => { setSize(g); setStep(3); }}
                data-testid={`estimator-size-${g.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className={`p-5 text-left border transition-all duration-200 hover:-translate-y-0.5 ${size === g ? "border-[#028090] bg-[#028090]/10 text-white" : "border-white/15 text-gray-300 hover:border-[#028090]/60 hover:text-white"}`}
              >
                <p className="font-display font-bold text-lg">{g}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {g === "1 Car" && "~220–280 sq ft"}
                  {g === "2 Car" && "~400–480 sq ft"}
                  {g === "3 Car" && "~600–720 sq ft"}
                  {g === "4+ Car / RV" && "~800+ sq ft"}
                </p>
              </button>
            ))}
          </div>
          <button onClick={() => setStep(1)} data-testid="estimator-back-2" className="text-gray-400 hover:text-white text-xs uppercase tracking-wider mt-6 transition-colors">← Back</button>
        </div>
      )}

      {step === 3 && (
        <div data-testid="estimator-step-3">
          <label className="flex items-center gap-2 text-white font-semibold mb-4 text-sm"><Wrench size={16} className="text-[#028090]" strokeWidth={1.5} /> What's the current condition?</label>
          <div className="space-y-3">
            {CONDITION_OPTIONS.map((c) => (
              <button
                key={c.id}
                onClick={() => { setCondition(c.id); setStep(4); }}
                data-testid={`estimator-condition-${c.id}`}
                className={`w-full p-4 text-left border transition-all duration-200 hover:-translate-y-0.5 ${condition === c.id ? "border-[#028090] bg-[#028090]/10" : "border-white/15 hover:border-[#028090]/60"}`}
              >
                <p className="font-display font-bold text-white">{c.label}</p>
                <p className="text-xs text-gray-500 mt-1">{c.desc}</p>
              </button>
            ))}
          </div>
          <button onClick={() => setStep(2)} data-testid="estimator-back-3" className="text-gray-400 hover:text-white text-xs uppercase tracking-wider mt-6 transition-colors">← Back</button>
        </div>
      )}
    </div>
  );
}
